import { Box } from '@mui/material';
import React from 'react';

import { CardBack } from '../art/CardArt';
import { CardImage } from '../art/CardImage';

interface Props {
    cardId: string;
    name: string;
    reversed: boolean;
    faceUp: boolean;
    /** Card width in px; the height follows the 200:340 ratio. */
    width?: number;
    onClick?: () => void;
    /** A soft pulsing halo, for cards waiting to be turned. */
    glow?: boolean;
    label?: string;
}

/** One card that turns over in 3D when `faceUp` changes. */
export const TarotCard: React.FC<Props> = ({
    cardId,
    name,
    reversed,
    faceUp,
    width = 120,
    onClick,
    glow,
    label,
}) => {
    const interactive = Boolean(onClick);
    return (
        <Box
            component={interactive ? 'button' : 'div'}
            type={interactive ? 'button' : undefined}
            onClick={onClick}
            aria-label={label ?? name}
            sx={{
                p: 0,
                border: 0,
                background: 'none',
                cursor: interactive ? 'pointer' : 'default',
                width,
                height: (width * 340) / 200,
                perspective: 900,
                flexShrink: 0,
                borderRadius: '10px',
                animation: glow
                    ? 'arcana-pulse 2.4s ease-in-out infinite'
                    : undefined,
                '@keyframes arcana-pulse': {
                    '0%, 100%': {
                        filter: 'drop-shadow(0 0 4px rgba(212,175,106,.25))',
                    },
                    '50%': {
                        filter: 'drop-shadow(0 0 16px rgba(212,175,106,.75))',
                    },
                },
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.9s cubic-bezier(.2,.8,.2,1)',
                    transform: faceUp ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    boxShadow: '0 6px 22px rgba(0,0,0,.55)',
                    borderRadius: '10px',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backfaceVisibility: 'hidden',
                    }}
                >
                    <CardBack />
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                    }}
                >
                    <CardImage
                        cardId={cardId}
                        name={name}
                        reversed={reversed}
                    />
                </Box>
            </Box>
        </Box>
    );
};
